import { useState, useEffect, useRef } from "react";
import Cookies from "js-cookie";
import { Modal, Spinner } from "react-bootstrap";
import { FiSearch, FiX, FiArrowLeft } from "react-icons/fi";
import { Link } from "react-router-dom";

import "../styles/searchbar.css";
import api from "../api.js";

const SearchModal = () => {
  const [show, setShow] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState({
    fields: [],
    subjects: [],
    reports: [],
  });
  const [loading, setLoading] = useState(false);

  const inputRef = useRef(null);
  const token = Cookies.get("access_token");

  const handleSearch = async (q) => {
    const searchText = q.trim();

    if (!searchText) {
      setResults({
        fields: [],
        subjects: [],
        reports: [],
      });
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      const res = await api.get("/search/", {
        params: {
          q: searchText,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setResults({
        fields: res.data?.fields || [],
        subjects: res.data?.subjects || [],
        reports: res.data?.reports || [],
      });
    } catch (err) {
      console.error("Search error:", err);

      setResults({
        fields: [],
        subjects: [],
        reports: [],
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const delay = setTimeout(() => {
      handleSearch(query);
    }, 400);

    return () => clearTimeout(delay);
  }, [query]);

  useEffect(() => {
    if (show) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }

    const handleEsc = (e) => {
      if (e.key === "Escape") {
        setShow(false);
      }
    };

    window.addEventListener("keydown", handleEsc);

    return () => {
      window.removeEventListener("keydown", handleEsc);
    };
  }, [show]);

  const totalResults =
    results.fields.length +
    results.subjects.length +
    results.reports.length;

  const closeModal = () => {
    setShow(false);
  };

  return (
    <>
      <button
        onClick={() => setShow(true)}
        className="search-btn"
        aria-label="جستجو"
      >
        <FiSearch size={19} />
      </button>

      <Modal
        show={show}
        onHide={closeModal}
        centered
        size="lg"
        dir="rtl"
        className="search-modal"
      >
        <Modal.Body className="search-modal-body">

          {/* Header */}
          <div className="search-modal-header">
            <div>
              <h5 className="search-modal-title">
                جستجو
              </h5>

              <p className="search-modal-subtitle">
                نام فیلد، موضوع یا گزارش موردنظر خود را جستجو کنید
              </p>
            </div>

            <button
              type="button"
              onClick={closeModal}
              className="search-close-btn"
              aria-label="بستن"
            >
              <FiX size={22} />
            </button>
          </div>

          {/* Search box */}
          <div className="search-modal-input-wrapper">

            <FiSearch className="search-modal-input-icon" size={21} />

            <input
              ref={inputRef}
              type="text"
              placeholder="مثلاً: هوش مصنوعی"
              className="search-modal-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              autoComplete="off"
            />

            {query && (
              <button
                type="button"
                className="search-clear-btn"
                onClick={() => setQuery("")}
                aria-label="پاک کردن"
              >
                <FiX size={18} />
              </button>
            )}

          </div>

          {/* Search hint */}
          {query.trim() && !loading && (
            <div className="search-query-info">
              <span>
                نتایج جستجو برای:
              </span>

              <strong>
                «{query.trim()}»
              </strong>
            </div>
          )}

          {/* Loading */}
          {loading && (
            <div className="search-loading">
              <Spinner
                animation="border"
                size="sm"
              />

              <span>
                در حال جستجو...
              </span>
            </div>
          )}

          {/* Results */}
          {!loading && totalResults > 0 && (
            <div className="search-results">

              <div className="search-result-count">
                {totalResults} نتیجه یافت شد
              </div>

              {/* Fields */}
              {results.fields.length > 0 && (
                <div className="search-section">

                  <div className="search-section-header field-header">
                    <div className="search-section-title">
                      <span className="search-section-icon">
                        ◈
                      </span>

                      <span>
                       میزها
                      </span>
                    </div>

                    <span className="search-section-count">
                      {results.fields.length}
                    </span>
                  </div>

                  <div className="search-result-list">

                    {results.fields.map((field) => (
                      <Link
                        key={`field-${field.id}`}
                        to={`/field/${field.id}`}
                        onClick={closeModal}
                        className="search-result-item"
                      >
                        <div className="search-result-content">
                          <span className="search-result-title">
                            {field.title}
                          </span>

                          <span className="search-result-type">
                            میز
                          </span>
                        </div>

                        <FiArrowLeft
                          className="search-result-arrow"
                          size={18}
                        />
                      </Link>
                    ))}

                  </div>
                </div>
              )}

              {/* Subjects */}
              {results.subjects.length > 0 && (
                <div className="search-section">

                  <div className="search-section-header subject-header">
                    <div className="search-section-title">
                      <span className="search-section-icon">
                        ◈
                      </span>

                      <span>
                        موضوعات
                      </span>
                    </div>

                    <span className="search-section-count">
                      {results.subjects.length}
                    </span>
                  </div>

                  <div className="search-result-list">

                    {results.subjects.map((subject) => (
                      <Link
                        key={`subject-${subject.id}`}
                        to={`/subject/${subject.id}`}
                        onClick={closeModal}
                        className="search-result-item"
                      >
                        <div className="search-result-content">
                          <span className="search-result-title">
                            {subject.title}
                          </span>

                          <span className="search-result-type">
                            موضوع
                          </span>
                        </div>

                        <FiArrowLeft
                          className="search-result-arrow"
                          size={18}
                        />
                      </Link>
                    ))}

                  </div>
                </div>
              )}

              {/* Reports */}
              {results.reports.length > 0 && (
                <div className="search-section">

                  <div className="search-section-header report-header">
                    <div className="search-section-title">
                      <span className="search-section-icon">
                        ◈
                      </span>

                      <span>
                        گزارش‌ها
                      </span>
                    </div>

                    <span className="search-section-count">
                      {results.reports.length}
                    </span>
                  </div>

                  <div className="search-result-list">

                    {results.reports.map((report) => (
                      <Link
                        key={`report-${report.id}`}
                        to={`/report/${report.id}`}
                        onClick={closeModal}
                        className="search-result-item"
                      >
                        <div className="search-result-content">
                          <span className="search-result-title">
                            {report.title}
                          </span>

                          <span className="search-result-type">
                            گزارش
                          </span>
                        </div>

                        <FiArrowLeft
                          className="search-result-arrow"
                          size={18}
                        />
                      </Link>
                    ))}

                  </div>
                </div>
              )}

            </div>
          )}

          {/* No results */}
          {!loading &&
            query.trim() &&
            totalResults === 0 && (
              <div className="search-empty">

                <div className="search-empty-icon">
                  <FiSearch size={30} />
                </div>

                <h6>
                  نتیجه‌ای یافت نشد
                </h6>

                <p>
                  عبارت دیگری را امتحان کنید.
                </p>

              </div>
            )}

          {/* Initial state */}
          {!loading &&
            !query.trim() && (
              <div className="search-initial">

                <FiSearch size={24} />

                <span>
                  عبارت موردنظر خود را وارد کنید
                </span>

              </div>
            )}

        </Modal.Body>
      </Modal>
    </>
  );
};

export default SearchModal;